import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import Edit from '@material-ui/icons/Edit';
import Save from '@material-ui/icons/Save';
import { MultiSelectField } from 'components/Form';
import { ProfileContext } from 'utils/context';
import { isCreativeProfile } from '../Onboarding/config';

const ValueList = ({ items, title }) => (
  <div className="mb-6">
    <p className="text-sm text-boulder uppercase mb-2">{title}</p>
    {items.length > 0 ? (
      <div className="flex flex-wrap">
        {items.map(({ label, value }) => (
          <span
            className="bg-gradient-easter-bostonBlue px-2 py-1 mr-2 mb-2 rounded text-white shadow"
            key={value}
          >
            {label}
          </span>
        ))}
      </div>
    ) : (
      <p className="text-dusty">Not provided</p>
    )}
  </div>
);

ValueList.propTypes = {
  items: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
};

const ProfessionalProfile = ({
  classificationOptions,
  isLoadingClassifications,
  isLoadingTargetMarkets,
}) => {
  const { isView, readOnly } = useContext(ProfileContext);
  const { handleSubmit, values } = useFormikContext();
  const [viewProfile, toggleForm] = useState(isView);
  const industryAreas = values.industry_areas || [];
  const professionalGoals = values.professional_goals || [];
  const targetMarkets = values.target_markets || [];
  const professionalSkills = values.professional_skills || [];
  const professionalRoles = values.professional_roles || [];
  const interestTopics = values.interest_topics || [];
  const isCreative = isCreativeProfile({ industry_areas: industryAreas });

  if (viewProfile || readOnly) {
    return (
      <div>
        <div className="flex justify-between">
          <h4 className="text-dusty text-lg font-semibold uppercase mb-4">
            Professional Profile
          </h4>
          {!readOnly && (
            <Edit
              className="text-dusty cursor-pointer hover:text-easter"
              onClick={() => toggleForm(false)}
              role="presentation"
            />
          )}
        </div>
        <ValueList title="Areas of the music industry" items={industryAreas} />
        {isCreative && (
          <>
            <ValueList title="Creative roles" items={professionalRoles} />
            <ValueList title="Genres you work in" items={interestTopics} />
          </>
        )}
        <ValueList
          title="What I hope to get from GLP"
          items={professionalGoals}
        />
        <ValueList
          title="Opportunities I am open to"
          items={professionalSkills}
        />
        <ValueList title="Markets I am interested in" items={targetMarkets} />
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex justify-between mb-8 sm:mb-0">
        <h5 className="text-lg mb-4">Professional Profile</h5>
        {isView && (
          <button
            className="flex text-dusty hover:text-easter justify-end focus:outline-none"
            type="button"
            onClick={() => {
              handleSubmit();
              toggleForm(true);
            }}
          >
            <Save />
            <span className="ml-2">Save Changes</span>
          </button>
        )}
      </div>
      <MultiSelectField
        name="industry_areas"
        label="Areas of the music industry (up to 3)"
        max={3}
        options={classificationOptions.industry_areas}
        placeholder="Select industry areas"
        creatable={false}
        isLoading={isLoadingClassifications}
      />
      {isCreative && (
        <>
          <MultiSelectField
            name="professional_roles"
            label="Creative roles (up to 3)"
            max={3}
            options={classificationOptions.professional_roles}
            placeholder="Select creative roles"
            creatable={false}
            isLoading={isLoadingClassifications}
          />
          <MultiSelectField
            name="interest_topics"
            label="Genres you work in (up to 5)"
            max={5}
            options={classificationOptions.interest_topics}
            placeholder="Select genres"
            creatable={false}
            isLoading={isLoadingClassifications}
          />
        </>
      )}
      <MultiSelectField
        name="professional_goals"
        label="What do you hope to get from GLP? (up to 3)"
        max={3}
        options={classificationOptions.professional_goals}
        placeholder="Select your goals"
        creatable={false}
        isLoading={isLoadingClassifications}
      />
      <MultiSelectField
        name="professional_skills"
        label="Opportunities you are open to"
        exclusiveValue="Not currently looking"
        options={classificationOptions.professional_skills}
        placeholder="Select opportunities"
        creatable={false}
        isLoading={isLoadingClassifications}
      />
      <MultiSelectField
        name="target_markets"
        label="Markets you are interested in (up to 5)"
        max={5}
        options={classificationOptions.target_markets}
        placeholder="Select target markets"
        creatable={false}
        isLoading={isLoadingTargetMarkets}
      />
    </div>
  );
};

ProfessionalProfile.propTypes = {
  classificationOptions: PropTypes.object.isRequired,
  isLoadingClassifications: PropTypes.bool,
  isLoadingTargetMarkets: PropTypes.bool,
};

ProfessionalProfile.defaultProps = {
  isLoadingClassifications: false,
  isLoadingTargetMarkets: false,
};

export default ProfessionalProfile;
